import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { getApiUrl } from '../helpers/helperApp';
import swal from 'sweetalert';

export const useServerPagination = ({
    apiEndpoint,
    apiKey,
    perPage = 25,
    debounceTime = 500,
    setLoadingTableExternal = null,
    currentPage: externalCurrentPage,
    setCurrentPage: setCurrentPageExternal = null,
    idUpdate = null,
    initialPage = 1
}) => {
    // States
    const [data, setData] = useState([]);
    const [internalCurrentPage, setInternalCurrentPage] = useState(initialPage);
    const currentPage = externalCurrentPage !== undefined ? externalCurrentPage : internalCurrentPage;
    const setPage = setCurrentPageExternal
        ? (page) => { setCurrentPageExternal(page); setInternalCurrentPage(page); }
        : setInternalCurrentPage;

    // perPage como estado para que sea reactivo cuando el usuario cambia el pageLength en la tabla
    const [perPageState, setPerPageState] = useState(perPage);

    const [totalPages, setTotalPages] = useState(0);
    const [totalRecords, setTotalRecords] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [loadingTable, setLoadingTable] = useState(false);
    const [paginationInfo, setPaginationInfo] = useState('');

    // Función para setLoading - usa la externa si se proporciona
    const setLoading = useCallback((value) => {
        setLoadingTable(value);
        if (setLoadingTableExternal) {
            setLoadingTableExternal(value);
        }
    }, [setLoadingTableExternal]);

    // Refs
    const searchTimeout = useRef(null);

    // Función principal de fetch
    const fetchData = useCallback(async (page, search, showLoading = true) => {
        if (showLoading) setLoading(true);
        try {
            const formData = new FormData();
            formData.append("validacion", apiKey());
            formData.append("page", page);
            formData.append("perPage", perPageState);
            if (search) {
                formData.append("searchTerm", search);
            }

            const url = `${getApiUrl(window.location.origin)}${apiEndpoint}`;
            const response = await axios.post(url, formData);
            if (response.data.success && Array.isArray(response.data.data)) {
                setData(response.data.data);
                const backendPagination = response.data.pagination || {};
                setTotalPages(backendPagination.totalPages || 0);
                setTotalRecords(backendPagination.totalRecords || 0);
            } else {
                swal("Error", "Error en la respuesta del servidor", "error");
                setData([]);
            }
        } catch (error) {
            swal("Error", "Error al cargar datos paginados", "error");
            setData([]);
        } finally {
            if (showLoading) setLoading(false);
        }
    }, [apiEndpoint, apiKey, perPageState, setLoading]);

    // Calcular paginationInfo cuando cambian los valores
    const updatePaginationInfo = useCallback(() => {
        if (totalRecords > 0) {
            const perPageNum = parseInt(perPageState);
            const startRecord = (currentPage - 1) * perPageNum + 1;
            const endRecord = Math.min(currentPage * perPageNum, totalRecords);
            setPaginationInfo(
                `Mostrando registros del ${startRecord} al ${endRecord} de un total de ${totalRecords} registros`
            );
        } else {
            setPaginationInfo('');
        }
    }, [totalRecords, currentPage, perPageState]);

    // Efecto para cargar datos cuando cambia la página o el término de búsqueda
    useEffect(() => {
        fetchData(currentPage, debouncedSearchTerm);
    }, [currentPage, debouncedSearchTerm, fetchData, idUpdate]);

    // Efecto para actualizar la información de paginación
    useEffect(() => {
        updatePaginationInfo();
    }, [updatePaginationInfo]);

    const handleSearchChange = useCallback((value) => {
        setSearchTerm(value);
        if (searchTimeout.current) {
            clearTimeout(searchTimeout.current);
        }
        searchTimeout.current = setTimeout(() => {
            setPage(1);
            setDebouncedSearchTerm(value);
        }, debounceTime);
    }, [debounceTime, setPage]);

    // Handler para búsqueda de DataTable (con debounce)
    const handleDataTableSearch = useCallback((searchValue) => {
        if (searchTimeout.current) {
            clearTimeout(searchTimeout.current);
        }

        searchTimeout.current = setTimeout(() => {
            setPage(1);
            setDebouncedSearchTerm(searchValue || '');
        }, debounceTime);
    }, [debounceTime, setPage]);

    const resetSearch = useCallback(() => {
        setSearchTerm('');
        setDebouncedSearchTerm('');
        setPage(1);
    }, [setPage]);

    // Handler para cambio de página
    const handlePageChange = useCallback((newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setPage(newPage);
        }
    }, [totalPages, setPage]);

    // Handler página siguiente
    const handleNextPage = useCallback(() => {
        if (currentPage < totalPages) {
            setPage(currentPage + 1);
        }
    }, [currentPage, totalPages, setPage]);

    // Handler página anterior
    const handlePreviousPage = useCallback(() => {
        if (currentPage > 1) {
            setPage(currentPage - 1);
        }
    }, [currentPage, setPage]);

    // Generar items de paginación con ...
    const getPaginationItems = useCallback(() => {
        const delta = 1;
        const left = currentPage - delta;
        const right = currentPage + delta + 1;
        const range = [];
        const rangeWithDots = [];
        let l;

        for (let i = 1; i <= totalPages; i++) {
            if (i === 1 || i === totalPages || (i >= left && i < right)) {
                range.push(i);
            }
        }

        for (const i of range) {
            if (l) {
                if (i - l === 2) {
                    rangeWithDots.push(l + 1);
                } else if (i - l !== 1) {
                    rangeWithDots.push('...');
                }
            }
            rangeWithDots.push(i);
            l = i;
        }

        return rangeWithDots;
    }, [currentPage, totalPages]);

    const reload = useCallback(() => {
        fetchData(currentPage, debouncedSearchTerm, false);
    }, [currentPage, debouncedSearchTerm, fetchData]);

    // Handler para cuando el usuario cambia el número de registros por página en la tabla
    const handlePageLengthChange = useCallback((newLength) => {
        setPerPageState(newLength);
        setPage(1);
    }, [setPage]);

    return {
        // Estados y datos
        data,
        loadingTable,
        currentPage,
        totalPages,
        totalRecords,
        paginationInfo,
        searchTerm,

        // Handlers de paginación y búsqueda
        handlePageChange,
        handleNextPage,
        handlePreviousPage,
        handleSearchChange,
        handleDataTableSearch,
        handlePageLengthChange,
        resetSearch,

        // Utilidades
        getPaginationItems,
        reload
    };
};

export default useServerPagination;